import { merge } from 'lodash-es';
import { CHART_TYPES } from '../enums';
import { dashedLegend } from './html-legend-striped.svg';
import { getVar } from './builder-helpers';

const onLegendClick = (chart, item, context) => {
  const { type } = chart.config;

  const options = {
    bubbles: false,
    composed: false,
  };

  context.dispatchEvent(new CustomEvent('chart-legend-click', options));

  if (type === 'pie' || type === 'doughnut') {
    // Pie and doughnut charts only have a single dataset and visibility is per item
    chart.toggleDataVisibility(item.index);
  } else {
    chart.setDatasetVisibility(item.datasetIndex, !chart.isDatasetVisible(item.datasetIndex));
  }
  chart.update();
};

/**
 * Assumption 1 - the max number of legend items we show is 10.
 * Assumption 2 - the max number of legend items we show in one row is 5.
 * Given the assumption, we need to calc the max width of an item, considering gap between items, width of the
 * color recantagle, gap between rectangle and text.
 * If we find that the total length of chars in the legend items is less than the available space, we do not
 * enforce any width (the items will just fit it!)
 * Otherwise we calculate the allowed width per legend item, and items with more text will have elipsis.
 *
 * @return {string}
 */
const calculateLegendItemWidth = ({ items, context }) => {
  const pxInChar = 5;
  const gapBetweenItems = 16;
  const colredRectangleWidth = 10;
  const gapBetweenRectangleAndText = 6;
  const fixedWidthPerItem = colredRectangleWidth + gapBetweenRectangleAndText;
  const globalMaxItemsPerRow = 5;
  const maxItemsPerRow = items.length < globalMaxItemsPerRow ? items.length : globalMaxItemsPerRow;

  const containerWidth = context.getBoundingClientRect().width;
  const totalCharsInText = items.reduce((prev, item) => prev + (item?.text || []).length, 0);
  const totalPxsForAllText = pxInChar * totalCharsInText;
  /* eslint-disable max-len */
  const availablePxForCharsInRow = Math.ceil((containerWidth - ((maxItemsPerRow - 1) * gapBetweenItems) - (maxItemsPerRow * fixedWidthPerItem)));

  if (totalPxsForAllText < availablePxForCharsInRow) { // enough space in row for ALL the items - do not enforce any widft
    return;
  }

  const maxWidthPerItem = Math.floor(availablePxForCharsInRow / maxItemsPerRow);
  return `${maxWidthPerItem}px`;
};

const renderLegend = ({ items, context }) => {
  const itemWidth = calculateLegendItemWidth({ items, context });
  const style = itemWidth ? `--legend-item-width:${itemWidth}` : ``;


  return `${items.map(item => {
    const dash = item && item.lineDash && item.lineDash.length;
    return `
    <li class="legend-item ${item && item.hidden ? 'disabled-legend-item' : ''}"> 
      <div
        class="legend-color-box ${dash ? 'dash' : ''}"
        style="--legend-color: ${item && item.fillStyle};">
        ${dash ? dashedLegend() : `<div></div>`}
      </div>
      <span class="legend-label" style="${style}">${item && item.text}</span>
    </li>`;
  }).join('')}`;
};

const htmlLegendPlugin = context => ({
  id: 'htmlLegend',
  afterUpdate(chart, args, options) {
    const legendContainer = context.shadowRoot.getElementById(options.containerID);
    const items = chart.options.plugins.legend.labels
      .generateLabels(chart)
      .filter(item => item.text);
    legendContainer.innerHTML = renderLegend({ items, context });

    if (items.length === 0) {
      const legendWrapper = context.shadowRoot.getElementById('legend-container');
      legendWrapper.style.display = 'none';
    }

    items.forEach((item, index) => {
      context.shadowRoot.querySelector(`.legend-item:nth-child(${index + 1})`)
        .addEventListener('click', () => onLegendClick(chart, item, context));
    });
  },
  afterRender(chart, args, options) {
    if (!chart.$legendRendered) {
      chart.$legendRendered = true;
      const legendContainer = context.shadowRoot.getElementById('legend-container');
      const realLegendContainer = context.shadowRoot.getElementById('real-legend-container');
      if (!context.isLegendBoxOpen) {
        const legendContainerHeight = legendContainer?.getBoundingClientRect()?.height;
        const realLegendContainerHeight = realLegendContainer?.getBoundingClientRect()?.height;
        if (legendContainerHeight && realLegendContainerHeight) {
          context.isLegendExpandIndicatorVisible = legendContainerHeight < realLegendContainerHeight;
        }
      }
    }
  }
});

/**
 * @constant labelsPlugin is a hard coded plugin from chart.js that can manipulate the labels.
 * You can choose the height, width label's font, color etc...
 * We declard on each series in "./basic-config-builder.js" that if the segmant axis is the Y axis, the 'categoryPercentage' of the datset is 1 == 100% and the 'barPercentage' is 0.4 == 40%.
 * That means that the 'free' space to render labels is 0.6 == 60% of the category.
 * Thats why I double @constant segmentHeight by 0.3, because its half of the free space left.
 */
const labelsPlugin = {
  id: 'labelsPlugin',
  afterDatasetsDraw: chart => {
    const {
      ctx,
      height,
      data,
      chartArea: { left },
      scales: { y },
    } = chart;

    const chartDataLength = data.datasets[0].data.length;
    const canvasHeight = height;
    const segmentHeight = canvasHeight / chartDataLength;
    let labelHeight = segmentHeight * 0.3;
    labelHeight = labelHeight > 12 ? 12 : labelHeight;
    ctx.save();

    if (chartDataLength <= 10) {
      data.datasets[0].data.forEach((dataPoint, index) => {
        ctx.font = `${labelHeight}px Open Sans`;
        ctx.fillStyle = getVar('--fc-color-gray-800');
        ctx.fillText(
          `${data.labels[index]}`,
          left,
          y.getPixelForValue(index) - (segmentHeight * 0.2375) //* For rendering a float label above the bar, I double the 'segmentHeight' by 0.2375 == 0.2375% of the 'segmentHeight'.
        );
      });
    }
  },
};

const pluginsSelector = ({ context, config }) => {
  if (config.series[0].seriesType !== CHART_TYPES.HORIZONTAL_BAR) {
    return [htmlLegendPlugin(context)];
  }
  return [labelsPlugin];
};

export default (config, chartJSConfig, context) => {
  if (context.isLegendHidden) {
    const noLegendObject = {
      options: {
        plugins: {
          legend: {
            display: false,
          }
        }
      }
    };
    return merge(chartJSConfig, noLegendObject);
  }
  const legendObject = {
    options: {
      plugins: {
        htmlLegend: {
          containerID: 'real-legend-container',
        },
        legend: {
          display: false,
        }
      }
    },
    plugins: pluginsSelector({ context, config })
  };

  // Hide legends when the chart is of unsupported type
  if (
    chartJSConfig?.data?.datasets[0].type === CHART_TYPES.TREEMAP ||
    chartJSConfig?.data?.datasets[0].type === CHART_TYPES.SPARKLINE ||
    chartJSConfig?.type === CHART_TYPES.SANKEY ||
    chartJSConfig?.type === CHART_TYPES.FUNNEL ||
    config?.series[0].seriesType === CHART_TYPES.HORIZONTAL_BAR
  ) {
    context.shadowRoot.getElementById('legend-container').style.display = 'none';
  }

  return merge(chartJSConfig, legendObject);
};
